/* global chrome */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getProfile, getSearchTypeFieldMappings } from '../../lib/ProfileDataService';

const LAST_SELECTED_PROFILE_KEY = 'lastSelectedProfile';

// Extension Notice Modal Component
const ExtensionNoticeModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 shadow-lg">
        <div className="bg-white rounded-lg">
          <div className="pb-3 flex-shrink-0 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-lg font-semibold">
                <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('chromeExtensionRequired')}
              </div>
              <button
                onClick={onClose}
                className="h-6 w-6 p-0 bg-transparent border-none cursor-pointer"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="space-y-4 px-6 pb-6">
            <p className="text-sm text-gray-600">
              {t('chromeExtensionRequiredMessage')}
            </p>
            <ul className="text-sm text-gray-600 space-y-2 ml-5 list-disc">
              <li>{t('chromeExtensionReason1')}</li>
              <li>{t('chromeExtensionReason2')}</li>
            </ul>
            
            {/* YouTube Tutorial Video - Clickable Thumbnail */}
            <a
              href="https://www.youtube.com/watch?v=Q6k9w-uRDvc"
              target="_blank"
              rel="noopener noreferrer"
              className="relative w-full aspect-video rounded-lg overflow-hidden bg-gray-900 flex items-center justify-center group block"
            >
              <img 
                src="https://img.youtube.com/vi/Q6k9w-uRDvc/sddefault.jpg"
                alt="PeleGoal Extension Installation Guide"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors shadow-lg">
                  <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
            </a>
            
            <div className="flex justify-center mt-6">
              <a
                href="https://chromewebstore.google.com/detail/pelegoal/nogjcmjlomdddffbooakogbmlchfmjmm"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium text-center"
              >
                {t('installExtension')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const submitToExtension = async (selectedProfile, resultUrl, aiGeneratedContent = '') => {
  if (!selectedProfile) {
    throw new Error('No profile selected');
  }

  try {
    // Save selected profile to localStorage for future use
    localStorage.setItem(LAST_SELECTED_PROFILE_KEY, selectedProfile.id);
    
    // Get field mappings for the selected profile's search type
    const mappings = await getSearchTypeFieldMappings(selectedProfile.searchType);
    console.log('Field mappings:', mappings);
    
    // Get full profile data
    const profile = await getProfile(selectedProfile.id);
    console.log('Profile data:', profile);
    
    // Build Chrome extension message
    const fields = {};
    
    // Go through each field in the profile.fields object
    if (profile.fields) {
      Object.keys(profile.fields).forEach(profileFieldKey => {
        const profileFieldValue = profile.fields[profileFieldKey];
        
        // Find the mapped array in mappings variable
        if (mappings[profileFieldKey] && Array.isArray(mappings[profileFieldKey])) {
          // Loop through each element in the mapping array
          mappings[profileFieldKey].forEach(mappedFieldName => {
            fields[mappedFieldName] = profileFieldValue;
          });
        }
      });
    }
    
    console.log('Chrome extension fields:', fields);
    
    // if fields contain firstName and lastName, concatenate them into fullName
    if (profile.fields.firstName && profile.fields.lastName) {
      fields.name = `${profile.fields.firstName} ${profile.fields.lastName}`;
    }

    // Add AI generated content to common message field names
    if (aiGeneratedContent) {
      // Common field names for message/content areas in contact forms
      const messageFieldNames = [
        'message',
        'inquiry', 
        'comment',
        'content',
        'description',
        'details',
        'body',
        'text',
        'notes',
        'msg',
        'info',
        'request',
        'textarea'
      ];
      
      // Add AI generated content to all common message field variations
      messageFieldNames.forEach(fieldName => {
        fields[fieldName] = aiGeneratedContent;
      });
      
      // Also keep the original field name for backward compatibility
      fields.aiGeneratedContent = aiGeneratedContent;
    }

    // Check if Chrome extension is available BEFORE opening tab
    if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
      console.warn('Chrome extension API not available');
      
      // Return special error object that calling component can handle to show modal
      const error = new Error('Chrome extension not installed');
      error.showExtensionModal = true;
      throw error;
    }

    // Open new tab only AFTER confirming extension is available
    window.open(resultUrl, '_blank');

    // TODO: Replace 'your-extension-id' with actual Chrome extension ID
    const extensionId = 'nogjcmjlomdddffbooakogbmlchfmjmm';
    
    // Send message to Chrome extension
    chrome.runtime.sendMessage(extensionId, {
      action: 'fillForm',
      fields: fields
    });
    console.log('Message sent to Chrome extension');
    return { success: true };
    
  } catch (error) {
    console.error('Error processing profile for Chrome extension:', error);
    throw error;
  }
};

// Export the modal component for use in other files
export { ExtensionNoticeModal };
